import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { carregarDispensasDoMes, carregarNomesAtuais, carregarPresencasDoMes } from "../firebase";
import { calcularFrequencia, classificar, FAIXAS_REFERENCIA, mediaPctTotal, somenteAtivos } from "../calculo";
import { useNomeNucleo } from "../NucleosContexto";
import { gerarExcel } from "../excel";
import { gerarPDFRelatorio, gerarPDFModalidade, RELATORIO_EQUIPE } from "../pdfRelatorio";
import { MESES, VERDE, CINZA, CINZA_ESC, ALERTA_CL, ALERTA, AZUL, TEXTO, argbParaCss } from "../tema";
import { Card, Btn, Sel, StatusBox, Carregando, Vazio } from "../componentes";
import { INTERVALOS_FAIXA } from "../componentes/LegendaClassificacao";
import useIsMobile from "../useIsMobile";
import SemNucleo from "../componentes/SemNucleo";

// ── Seção: Gerar Relatório ───────────────────────────────────
export default function SecaoRelatorio({ userNucleo, isAdmin, nivelAcesso }) {
  const isMobile = useIsMobile();
  const nomeNucleo = useNomeNucleo();
  const anoAtual = new Date().getFullYear();
  const mesAtual = new Date().getMonth();
  const [mes, setMes] = useState(String(mesAtual));
  const [ano, setAno] = useState(String(anoAtual));
  // Núcleo travado no núcleo do coordenador; admin pode escolher.
  // Núcleo vem do seletor global do cabeçalho (userNucleo). Não há mais
  // estado/seletor próprio — os dados recarregam quando o núcleo global muda
  // (ver dependências do useEffect de busca).
  const nucleo = userNucleo || "";
  const [status, setStatus] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [preview, setPreview] = useState(null);
  const [carregando, setCarregando] = useState(false);
  // Dispensas do mês, buscadas junto com a presença — reaproveitadas pelos
  // gráficos e na hora de gerar o Excel, sem repetir a consulta.
  const [dispensasCache, setDispensasCache] = useState([]);
  // Os nomes do cadastro vão para o Excel junto com as dispensas.
  // Sem eles, a planilha identificava a pessoa pelo nome gravado no
  // registro de presença, e a dispensa escrita com o nome do cadastro
  // não casava: a tela abonava e o arquivo não.
  const [nomesCache, setNomesCache] = useState(null);
  const [graficos, setGraficos] = useState(null); // { porModalidade, porFaixa }
  // Resultado completo do calcularFrequencia — reaproveitado pelo relatório
  // por modalidade, sem rodar o cálculo de novo.
  const [calcCache, setCalcCache] = useState(null); // { totalTreinos, membros }
  const [modalidadeRelatorio, setModalidadeRelatorio] = useState("");
  const [gerandoModalidadePDF, setGerandoModalidadePDF] = useState(false);
  const [gerandoPDF, setGerandoPDF] = useState(false);
  // Só admin, coordenador geral e coordenador geram o relatório PDF
  // institucional. Coordenador técnico NÃO gera (nem outros perfis).
  const podeGerarPDF = ["admin", "coordenador_geral", "coordenador"].includes(nivelAcesso);

  const anos = [anoAtual - 1, anoAtual, anoAtual + 1].map(String);

  function limparResultados() {
    setPreview(null);
    setGraficos(null);
    setCalcCache(null);
    setStatus(null);
  }

  // Carga automática: ao abrir a aba e sempre que mês/ano/núcleo mudarem,
  // os dados já carregam sozinhos — sem precisar de botão "Buscar". O núcleo
  // vem do seletor global (userNucleo); trocar lá recarrega aqui.
  useEffect(() => {
    if (!nucleo) return;
    buscarPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, ano, userNucleo]);

  async function buscarPreview() {
    setCarregando(true);
    setStatus({ tipo: "info", msg: "Buscando dados de presença..." });
    try {
      const mesNum = parseInt(mes);
      const anoNum = parseInt(ano);

      // O mês é pedido ao banco, e não separado no navegador depois de
      // baixar o histórico inteiro. A consulta é a mesma das outras telas,
      // então as duas continuam contando exatamente os mesmos registros.
      const filtrados = await carregarPresencasDoMes(nucleo, mesNum, anoNum);

      // NÃO fazer dedup manual aqui. O calcularFrequencia (fonte única) já
      // deduplica por dia internamente (Set de dateKey), exatamente como a
      // Presença Diária faz. Fazer um dedup manual com chave própria
      // (email_data) descartava registros legítimos e fazia o Relatório
      // divergir da Presença Diária. Passamos os registros FILTRADOS crus,
      // idêntico ao que a Presença Diária passa.

      const pessoas = new Set(filtrados.map(r => r.userName || r.email));
      // "Dias de treinamento/atividade": conta os dias DISTINTOS com registro,
      // mas ignora sábado/domingo — um registro de fim de semana não é dia de
      // treino (não conta no denominador) e não deve inflar este número.
      const dias = new Set(
        filtrados
          .map(r => r.dateKey || (r.data ? r.data.split("/").reverse().join("-") : ""))
          .filter(Boolean)
          .filter(iso => {
            const dow = new Date(iso + "T12:00:00").getDay();
            return dow >= 1 && dow <= 5; // seg–sex
          })
      );

      // Total de registros únicos por pessoa+dia (só para o card informativo),
      // usando a MESMA normalização de dia do cálculo.
      const paresUnicos = new Set(
        filtrados.map(r => {
          const dk = r.dateKey || (r.data ? r.data.split("/").reverse().join("-") : "");
          return `${r.userName || r.email}__${dk}`;
        })
      );

      setPreview({ total: paresUnicos.size, pessoas: pessoas.size, dias: dias.size, registros: filtrados });

      // Busca as dispensas do mês junto (reaproveitadas no Excel depois,
      // sem repetir a consulta) e já roda o cálculo pros gráficos.
      // O mês vem do banco, como a presença.
      const dispensasDoMes = await carregarDispensasDoMes(nucleo, mesNum, anoNum);
      setDispensasCache(dispensasDoMes);

      // Nomes ATUAIS do cadastro (email -> nome completo). Faz o relatório
      // exibir sempre o nome completo do cadastro, mesmo que registros antigos
      // tenham sido gravados com nome incompleto.
      const nomesAtuais = await carregarNomesAtuais(nucleo);
      setNomesCache(nomesAtuais);

      // tempoReal desligado: o relatório é documento. O painel conta a
      // presença de hoje na hora, aqui o dia só entra depois das 18h30.
      const calc = calcularFrequencia({ registros: filtrados, dispensas: dispensasDoMes, mes: mesNum, ano: anoNum, nomesAtuais, tempoReal: false });
      const atletas = calc.membros.filter(m => m.grupo === "ATLETA");

      // Gráfico 1: frequência média por modalidade.
      // Agrupa e usa mediaPctTotal, a mesma fonte do resto do painel: o
      // atleta excluído no meio do mês não entra na média da modalidade.
      const porMod = {};
      atletas.forEach(m => { (porMod[m.modalidade] = porMod[m.modalidade] || []).push(m); });
      const porModalidade = Object.entries(porMod)
        .map(([modalidade, lista]) => ({
          modalidade,
          media: Math.round((mediaPctTotal(lista) ?? 0) * 100),
          atletas: somenteAtivos(lista).length,
        }))
        .filter(x => x.atletas > 0)
        .sort((a, b) => a.modalidade.localeCompare(b.modalidade));

      // Distribuição por faixa — exibida do PIOR pro melhor (Insuficiente
      // primeiro). O rótulo de cada barra já traz o intervalo percentual
      // (legenda embutida — sem quadro de legenda separado).
      const contagem = {};
      atletas.forEach(m => { contagem[m.classif.label] = (contagem[m.classif.label] || 0) + 1; });
      const FAIXAS_PIOR_PRIMEIRO = [...FAIXAS_REFERENCIA].reverse();
      const porFaixa = FAIXAS_PIOR_PRIMEIRO.map(f => ({
        faixa: `${f.label}  ·  ${INTERVALOS_FAIXA[f.label]}`,
        qtd: contagem[f.label] || 0,
        cor: argbParaCss(classificar(f.ref).cor),
      }));

      // Quadros por modalidade: cada modalidade isolada, atletas ordenados
      // do Insuficiente ao Excelente (pior % primeiro), com a classificação
      // de cada um.
      const porModalidadeDetalhe = [...new Set(atletas.map(m => m.modalidade))]
        .sort((a, b) => a.localeCompare(b, "pt-BR"))
        .map(mod => ({
          modalidade: mod,
          atletas: atletas
            .filter(m => m.modalidade === mod)
            .sort((x, y) => x.pctTotal - y.pctTotal)
            .map(m => ({
              nome: m.nome,
              presencas: m.presEfetiva, faltas: m.faltas,
              jc: m.jc, jm: m.jm, ji: m.ji,
              pct: Math.round(m.pctTotal * 100),
              cor: argbParaCss(m.classif.cor),
              bg: argbParaCss(m.classif.bg),
              label: m.classif.label,
            })),
        }));

      // Frequência da equipe técnica (técnicos, preparadores físicos e
      // auxiliares) — coordenação NÃO entra no quadro de presença.
      // Ordem definida pelo programa: preparação física (e auxiliar)
      // primeiro, depois cada modalidade em ordem alfabética, com o
      // técnico antes do auxiliar dentro de cada uma.
      const ehPreparacao = m => {
        const p = (m.perfil || "").toLowerCase();
        const md = (m.modalidade || "").toLowerCase();
        return p.includes("preparador") || p.includes("preparadora") || md.includes("prepara");
      };
      const ehAuxiliar = m => (m.perfil || "").toLowerCase().includes("auxiliar");
      const equipeTecnica = calc.membros
        .filter(m => m.grupo === "TECNICO")
        .sort((a, b) => {
          const pa = ehPreparacao(a) ? 0 : 1;
          const pb = ehPreparacao(b) ? 0 : 1;
          if (pa !== pb) return pa - pb;
          const cmpMod = (a.modalidade || "").localeCompare(b.modalidade || "", "pt-BR");
          if (cmpMod !== 0) return cmpMod;
          const auxA = ehAuxiliar(a) ? 1 : 0;
          const auxB = ehAuxiliar(b) ? 1 : 0;
          if (auxA !== auxB) return auxA - auxB;
          return a.nome.localeCompare(b.nome, "pt-BR");
        })
        .map(m => ({
          nome: m.nome, perfil: m.perfil, modalidade: m.modalidade,
          presencas: m.presEfetiva, faltas: m.faltas,
          jc: m.jc, jm: m.jm, ji: m.ji,
          pct: Math.round(m.pctTotal * 100),
          cor: argbParaCss(m.classif.cor),
          bg: argbParaCss(m.classif.bg),
          label: m.classif.label,
        }));

      setGraficos({ porModalidade, porFaixa, porModalidadeDetalhe, equipeTecnica });
      setCalcCache({ totalTreinos: calc.totalTreinos, membros: calc.membros });
      setModalidadeRelatorio(porModalidade[0]?.modalidade || "");
      setStatus({ tipo: "ok", msg: `${filtrados.length} registros encontrados para ${MESES[mesNum]}/${anoNum}.` });
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Erro ao buscar dados: " + (e.message || "verifique a conexão.") });
      setPreview(null);
      setGraficos(null);
      setCalcCache(null);
    } finally {
      setCarregando(false);
    }
  }

  async function confirmarGeracao() {
    if (!preview?.registros?.length) return;
    setConfirmando(false);
    setStatus({ tipo: "info", msg: "Gerando planilha..." });
    try {
      // Reaproveita as dispensas já buscadas em buscarPreview — evita
      // consultar o Firestore de novo pela mesma informação.
      const nomeArquivo = await gerarExcel({
        registros: preview.registros,
        dispensas: dispensasCache,
        nucleo,
        nucleoNome: nomeNucleo(nucleo),
        mes: parseInt(mes),
        ano: parseInt(ano),
        nomesAtuais: nomesCache,
      });
      setStatus({
        tipo: "ok",
        msg: `✅ Arquivo "${nomeArquivo}" gerado e baixado!`,
      });
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Erro ao gerar Excel: " + (e.message || "tente novamente.") });
    }
  }

  async function baixarPDF() {
    if (!calcCache) return;
    setGerandoPDF(true);
    setStatus({ tipo: "info", msg: "Gerando relatório PDF..." });
    try {
      const nomeArquivo = await gerarPDFRelatorio({
        totalTreinos: calcCache.totalTreinos,
        membros: calcCache.membros,
        nucleo,
        nucleoNome: nomeNucleo(nucleo),
        mes: parseInt(mes),
        ano: parseInt(ano),
      });
      setStatus({ tipo: "ok", msg: `✅ Relatório "${nomeArquivo}" gerado e baixado!` });
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Erro ao gerar PDF: " + (e.message || "tente novamente.") });
    } finally {
      setGerandoPDF(false);
    }
  }


  // Mesmo conteúdo do Excel por modalidade, no padrão visual do PDF geral
  // (logomarca, cabeçalho e tabela iguais), porém sem capa.
  async function baixarPDFModalidade() {
    if (!calcCache || !modalidadeRelatorio) return;
    setGerandoModalidadePDF(true);
    const nomeDoRecorte = modalidadeRelatorio === RELATORIO_EQUIPE
      ? "da equipe técnica"
      : `de ${modalidadeRelatorio}`;
    setStatus({ tipo: "info", msg: `Gerando PDF ${nomeDoRecorte}...` });
    try {
      const nomeArquivo = await gerarPDFModalidade({
        totalTreinos: calcCache.totalTreinos,
        membros: calcCache.membros,
        nucleo,
        nucleoNome: nomeNucleo(nucleo),
        mes: parseInt(mes),
        ano: parseInt(ano),
        modalidade: modalidadeRelatorio,
      });
      setStatus({ tipo: "ok", msg: `✅ Arquivo "${nomeArquivo}" gerado e baixado!` });
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Erro ao gerar PDF por modalidade: " + (e.message || "tente novamente.") });
    } finally {
      setGerandoModalidadePDF(false);
    }
  }

  // Sem núcleo escolhido, a tela diz isso em vez de carregar o de
  // outra pessoa. Depois dos hooks, de propósito: sair antes deles
  // quebraria a ordem que o React exige.
  if (!nucleo) return <SemNucleo oQueFaria="gerar o relatório deste núcleo" />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ── Configurar + buscar ─────────────────────────────── */}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Configurar relatório</div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <Sel label="Mês" value={mes} onChange={v => { setMes(v); limparResultados(); }}
            options={MESES.map((m, i) => ({ value: String(i), label: m }))} />
          <Sel label="Ano" value={ano} onChange={v => { setAno(v); limparResultados(); }}
            options={anos} />
        </div>
        {carregando && <Carregando linhas={5} />}
        {/* As duas réguas convivem de propósito, e sem esta linha a
            diferença vira dúvida de suporte: o coordenador veria 85% no
            painel e 84% aqui, no mesmo minuto, sem explicação à vista. */}
        <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 10, lineHeight: 1.5 }}>
          O relatório fecha o dia de hoje às 18h30, quando o treino termina.
          Nas demais telas a presença de hoje já conta assim que o atleta registra,
          então durante a tarde os percentuais podem diferir.
        </div>
      </Card>

      {/* ── Resultados: números + gráficos, num único card ─── */}
      {preview && (
        <Card>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2 }}>
            Resultados · {MESES[parseInt(mes)]}/{ano}
          </div>
          <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 16 }}>Núcleo {nomeNucleo(nucleo)}</div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 12 }}>
            {[
              { label: "Registros únicos", valor: preview.total, cor: VERDE },
              { label: "Dias de treinamento/atividade", valor: preview.dias, cor: "#0891B2" },
            ].map(m => (
              <div key={m.label} style={{ background: CINZA, borderRadius: 8, padding: 14, textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: m.cor }}>{m.valor}</div>
                <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 2 }}>{m.label}</div>
              </div>
            ))}
          </div>

          {graficos && (
            <>
              <div style={{ borderTop: "1px solid #E5E7EB", margin: "20px 0 16px" }} />
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>Frequência média por modalidade</div>
              <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 10 }}>
                Média do % total (presença + justificadas) entre os atletas de cada modalidade
              </div>
              {graficos.porModalidade.length === 0 ? (
                <Vazio icone="📊" titulo="Nenhum atleta com registros neste mês">
                  Pode ser mês sem treino lançado, ou núcleo sem atleta cadastrado. Confira o mês
                  escolhido acima e a aba Gerenciar usuários.
                </Vazio>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(120, graficos.porModalidade.length * 52)}>
                  <BarChart data={graficos.porModalidade} layout="vertical" margin={{ top: 4, right: 44, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#EEF2F7" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: CINZA_ESC }} unit="%" axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="modalidade" width={110} tick={{ fontSize: 12.5, fill: TEXTO, fontWeight: 600 }} axisLine={false} tickLine={false} />
                    <Bar dataKey="media" fill={AZUL} radius={[0, 6, 6, 0]} barSize={20}>
                      <LabelList dataKey="media" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 12, fontWeight: 700, fill: TEXTO }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}

              <div style={{ borderTop: "1px solid #E5E7EB", margin: "20px 0 16px" }} />
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>Distribuição por faixa de classificação</div>
              <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 10 }}>
                Quantidade de atletas por faixa de classificação
              </div>
              <ResponsiveContainer width="100%" height={Math.max(150, graficos.porFaixa.length * 44)}>
                <BarChart data={graficos.porFaixa} layout="vertical" margin={{ top: 4, right: 44, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#EEF2F7" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: CINZA_ESC }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="faixa" width={182} tick={{ fontSize: 11.5, fill: TEXTO, fontWeight: 600 }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => [v, "Atletas"]} cursor={{ fill: "#F4F6F9" }} />
                  <Bar dataKey="qtd" radius={[0, 6, 6, 0]} barSize={20}>
                    {graficos.porFaixa.map((entry, i) => <Cell key={i} fill={entry.cor} />)}
                    <LabelList dataKey="qtd" position="right" style={{ fontSize: 12, fontWeight: 700, fill: TEXTO }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Quadros por modalidade — atletas do Insuficiente ao Excelente */}
              <div style={{ borderTop: "1px solid #E5E7EB", margin: "20px 0 16px" }} />
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>Atletas por modalidade</div>
              <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 12 }}>
                Cada modalidade isolada, do menor pro maior % de frequência
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {graficos.porModalidadeDetalhe.map(g => (
                  <div key={g.modalidade} style={{ border: "1px solid #E5E7EB", borderRadius: 10, overflow: "hidden" }}>
                    <div style={{ background: "#EAF0FA", padding: "8px 14px", fontSize: 13, fontWeight: 700, color: AZUL }}>
                      {g.modalidade} · {g.atletas.length} atleta{g.atletas.length > 1 ? "s" : ""}
                    </div>
                    <div style={{ padding: "6px 14px" }}>
                      {g.atletas.map(a => (
                        <div key={a.nome} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 52px" : "1fr 150px 52px", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid #F1F5F9", fontSize: 13 }}>
                          <span>
                            <span style={{ fontWeight: 600, color: TEXTO }}>{a.nome}{a.inativo ? <span style={{ color: "#94A3B8", fontWeight: 400, fontStyle: "italic" }}> (inativo)</span> : null}</span>
                            <span style={{ display: "block", fontSize: 11.5, color: CINZA_ESC, marginTop: 1 }}>
                              Presenças: {a.presencas} · Justif.: {a.jc + a.jm + a.ji} · Não justif.: {a.faltas}
                            </span>
                            {isMobile && <span style={{ display: "block", marginTop: 2 }}><span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 8px", borderRadius: 20, background: a.bg, color: a.cor }}>{a.label}</span></span>}
                          </span>
                          {!isMobile && <span><span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: a.bg, color: a.cor, whiteSpace: "nowrap" }}>{a.label}</span></span>}
                          <span style={{ fontWeight: 700, color: a.cor, textAlign: "right" }}>{a.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Equipe técnica e coordenação — isolada dos atletas */}
              {graficos.equipeTecnica.length > 0 && (
                <>
                  <div style={{ borderTop: "1px solid #E5E7EB", margin: "20px 0 16px" }} />
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>Frequência da equipe técnica</div>
                  <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 10 }}>
                    Técnicos, preparadores físicos e auxiliares têm acompanhamento separado dos atletas. Coordenação não entra no quadro de presença.
                  </div>
                  <div style={{ border: "1px solid #E5E7EB", borderRadius: 10, overflow: "hidden" }}>
                    <div style={{ background: "#EDE9FE", padding: "8px 14px", fontSize: 13, fontWeight: 700, color: "#7C3AED" }}>
                      Equipe técnica · {graficos.equipeTecnica.length} membro{graficos.equipeTecnica.length > 1 ? "s" : ""}
                    </div>
                    <div style={{ padding: "6px 14px" }}>
                      {graficos.equipeTecnica.map(m => (
                        <div key={m.nome} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 52px" : "1fr 150px 52px", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid #F1F5F9", fontSize: 13 }}>
                          <span>
                            <span style={{ fontWeight: 600, color: TEXTO }}>{m.nome}{m.inativo ? <span style={{ color: "#94A3B8", fontWeight: 400, fontStyle: "italic" }}> (inativo)</span> : null}</span>
                            <span style={{ display: "block", fontSize: 11.5, color: CINZA_ESC, marginTop: 1 }}>
                              {m.perfil}{m.modalidade ? ` · ${m.modalidade}` : ""} — Presenças: {m.presencas} · Justif.: {m.jc + m.jm + m.ji} · Não justif.: {m.faltas}
                            </span>
                            {isMobile && <span style={{ display: "block", marginTop: 2 }}><span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 8px", borderRadius: 20, background: m.bg, color: m.cor }}>{m.label}</span></span>}
                          </span>
                          {!isMobile && <span><span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: m.bg, color: m.cor, whiteSpace: "nowrap" }}>{m.label}</span></span>}
                          <span style={{ fontWeight: 700, color: m.cor, textAlign: "right" }}>{m.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </Card>
      )}

      {/* ── Exportar: por modalidade primeiro, completo depois ── */}
      {preview?.registros?.length > 0 && (
        <Card>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Exportar relatório</div>

          {calcCache && graficos?.porModalidade?.length > 0 && (
            <>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>Relatório por modalidade</div>
              <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 12, lineHeight: 1.55 }}>
                Cada modalidade traz só os atletas dela. A equipe técnica tem documento próprio,
                com técnicos, auxiliares e preparação física de todas as modalidades.
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                <Sel label="Modalidade ou equipe" value={modalidadeRelatorio} onChange={setModalidadeRelatorio}
                  options={[
                    ...graficos.porModalidade.map(m => ({ value: m.modalidade, label: m.modalidade })),
                    { value: RELATORIO_EQUIPE, label: "Equipe técnica (todas)" },
                  ]} style={{ minWidth: 220 }} />
                <Btn onClick={baixarPDFModalidade} disabled={gerandoModalidadePDF || !modalidadeRelatorio} style={{ background: "#7C3AED" }}>
                  {gerandoModalidadePDF ? "Gerando..." : "📄 Baixar PDF"}
                </Btn>
              </div>
              <div style={{ borderTop: "1px solid #E5E7EB", margin: "18px 0 16px" }} />
            </>
          )}

          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Relatório completo</div>

          {podeGerarPDF && calcCache && (
            <Btn onClick={baixarPDF} disabled={gerandoPDF} style={{ fontSize: 15, padding: "12px 24px", background: "#2E4E9E", width: "100%", marginBottom: 10 }}>
              {gerandoPDF ? "Gerando PDF..." : "📕 Baixar relatório PDF (institucional)"}
            </Btn>
          )}

          {!confirmando ? (
            <Btn onClick={() => setConfirmando(true)} style={{ fontSize: 15, padding: "12px 24px", background: "#0891B2", width: "100%" }}>
              📊 Gerar Excel completo ({preview.total} registros)
            </Btn>
          ) : (
            <div style={{ background: ALERTA_CL, border: `1px solid ${ALERTA}44`, borderRadius: 10, padding: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#92400E", marginBottom: 6 }}>Confirmar geração</div>
              <div style={{ fontSize: 13, color: "#78350F", marginBottom: 12, lineHeight: 1.6 }}>
                <strong>{preview.total}</strong> registros · <strong>{preview.dias}</strong> dias
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <Btn onClick={confirmarGeracao} style={{ background: VERDE }}>⬇️ Gerar e baixar Excel</Btn>
                <Btn onClick={() => setConfirmando(false)} variant="secondary">Cancelar</Btn>
              </div>
            </div>
          )}
        </Card>
      )}

      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

    </div>
  );
}
